import React, { useContext, useEffect, useReducer } from 'react';
import axios from 'axios';
import cloneDeep from 'lodash.clonedeep';
import CrossMediaContext from './crossMediaContext';
import CrossMediaReducer from './crossMediaReducer';
import AuthContext from '@context/auth/AuthContext';
import MainContext from '@context/main/MainContext';
import { CROSSMEDIATYPE } from '../../components/Utilities/NamingConversions';
import { REFRESH } from '../types';
import { EVENT_NAMES } from '@constants/trackingConstants';
import { CrossMediaService } from '@api/cmp';
import { v4 as uuidv4 } from 'uuid';
import { trackerService } from '@api/tracker';

let emptyMediaMix = [
  {
    values: [1],
    labels: ['Campaign'],
    type: 'pie',
    sort: false,
    text: ['No Budget Allocated'],
    textinfo: 'text',
    textposition: 'inside',
    hoverinfo: 'text',
  },
];

function CrossMediaState(props) {
  const authContext = useContext(AuthContext);
  const mainContext = useContext(MainContext);
  const crossMediaService = new CrossMediaService();

  // This useEffect is required to update company name, when user switches accounts.
  useEffect(() => {
    dispatch({
      type: 'SET_SELECTED_DATA',
      payload: authContext?.userInfo?.company,
    });
  }, [authContext]);

  const defaultCrossStates = {
    budgetIndex: 1, // used in budget slider -- represents the index
    timeScale: 'campaign', // objective curves; weekly / campaign
    campReach: 'campReach',
    campSales: 'campSales',
  };

  const defaultContext = {
    curvesGraph: [{}],
    flightGraph: [],
    flightTable: [],
    mediaMixGraph: emptyMediaMix,
    crossStates: cloneDeep(defaultCrossStates),
    medias: [],
    MMMs: [],
    DMAs: [],
    selectedData:
      authContext.userCountryCode === 'ca' && authContext.userInfo?.company
        ? authContext.userInfo.company
        : 'Arima',
    publisher: 'Arima',
    preset: 'Default',
    presets: ['Default'],
  };

  const initialState = {
    penetration: {},
    publishers: [],
    industryWeights: { Custom: [50, 50] },
    previousTab: false,
    selectedTab: false,
    tabs: {},
    workflowId: null,
    tabWorkflowId: null,
  };

  const [state, dispatch] = useReducer(CrossMediaReducer, {
    defaultContext: defaultContext,
    defaultCrossStates: defaultCrossStates,
    ...defaultContext,
    ...initialState,
  });

  const useState = (key) => {
    // used like react.useState, but will store state in context manager
    // to allow for context switching
    const get = (key) => state.crossStates[key];
    const set = (key, value) => {
      dispatch({
        type: 'SET_CROSS_STATE',
        payload: { key: key, value: value },
      });
    };

    return [get(key), (value) => set(key, value)];
  };

  const dumpContext = (deepCopy = false) => {
    // returns a shallow clone of the current context
    // if deepCopy is true, will return a deep copy of the context
    const context = {};
    for (const key of Object.keys(state.defaultContext))
      context[key] = state[key];

    return deepCopy ? cloneDeep(context) : context;
  };

  const loadContext = (context) => {
    // loads the given context into the current context
    //    curvesGraph: [{}],
    //    flightGraph: [],
    //    flightTable: [],
    //    mediaMixGraph: emptyMediaMix,
    //    crossStates: cloneDeep(defaultCrossStates),
    //    medias: [],
    //    publishers: [],
    //    MMMs: [],
    //    selectedData: authContext.userInfo.company : 'Arima',
    dispatch({
      type: 'SET_CONTEXT',
      payload: context,
    });
  };

  ////////////////
  // POST REQUESTS

  /**
   * Fetches the list of presets based on the current publisher and user context, and updates the cross context with the relevant data.
   *
   * This function retrieves the user's authentication token, queries the available presets using the current `publisher` and user-specific information,
   * and then updates the state with the fetched preset results.
   *
   * @async
   * @returns {Promise<void>} Resolves when the preset data has been successfully fetched and stored.
   */
  const updatePresets = async (publisher) => {
    try {
      mainContext.loadingIncrement(CROSSMEDIATYPE);
      const token = await authContext.getIdToken();
      const data = await crossMediaService.getPresets(token, {
        presetCountryCode: authContext.userCountryCode,
        publisher,
        kwargs: mainContext.getPostKWArgs(),
      });
      setPresets(data);
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      mainContext.loadingDecrement(CROSSMEDIATYPE);
    }
  };

  const getCrossMediaPenetration = async (
    medias,
    breakdown,
    dmas,
    { type = CROSSMEDIATYPE } = {}
  ) => {
    try {
      mainContext.loadingIncrement(type);
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_cross_media_penetration',
        {
          ids: mainContext.ids,
          medias: medias,
          breakdown: breakdown,
          dmas: dmas,
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'SET_PENETRATION',
        payload: res.data.penetration,
      });
      return res.data;
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      mainContext.loadingDecrement(type);
    }
  };

  const getCrossMediaPlan = async (
    crossMediaCampaign,
    index = false,
    medias
  ) => {
    try {
      mainContext.loadingIncrement(CROSSMEDIATYPE);
      let getPenetration = {};
      getPenetration = await getCrossMediaPenetration(
        medias,
        crossMediaCampaign.breakdown,
        crossMediaCampaign.dmas
      );
      const mediaPenetration = {
        ...getPenetration.penetration,
      };
      crossMediaCampaign = {
        ...crossMediaCampaign,
        penetration: mediaPenetration,
        medias: medias,
        countryCode: authContext.userCountryCode,
        kwargs: mainContext.getPostKWArgs(),
      };
      const token = await authContext.getIdToken();
      const { data } = await axios.post(
        '/get_cross_media_plan',
        crossMediaCampaign,
        {
          headers: { Authorization: token },
        }
      );
      if (!index) {
        let max = Math.max.apply(
          null,
          Object.values(state.tabs).map((t) => t.index)
        );
        index = max <= 0 ? 1 : max + 1;
      }
      let tab = {
        index: index,
        name: crossMediaCampaign.name,
        output: data.output,
        budget: data.budget,
        sliderMarks: data.budget.weekly.map((x, i) => ({
          budget: x,
          index: i,
        })),
        weeks: data.weeks,
        objective: crossMediaCampaign.objective,
        attention: crossMediaCampaign.attention,
        industryName: crossMediaCampaign.industry_name,
        industryWeights: crossMediaCampaign.objective_weights,
        population: data.population,
        colors: getSelectedMedias().map((media) => media.color),
        context: {},
        campaignBudget: crossMediaCampaign.campaignBudget,
        advanced: crossMediaCampaign.advanced,
        breakdown: crossMediaCampaign.breakdown,
        dmas: crossMediaCampaign.dmas,
      };
      if (tab.breakdown === false || tab.breakdown === undefined) {
        dispatch({ type: 'PUSH_TAB', payload: tab });
        dispatch({ type: 'SWITCH_TAB', payload: tab.index });
        dispatch({
          type: 'SET_CROSS_STATE',
          payload: { key: 'budgetIndex', value: tab.sliderMarks.length - 1 },
        });
      } else {
        window.open(tab.output.breakdown_url);
      }
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      mainContext.loadingDecrement(CROSSMEDIATYPE);
    }
  };

  const getCrossMediaParams = async ({
    type = CROSSMEDIATYPE,
    idsData = false,
  } = {}) => {
    try {
      mainContext.loadingIncrement(type);
      const countryCode = idsData
        ? idsData.dataset.includes('ca')
          ? 'ca'
          : 'us'
        : authContext.userCountryCode;
      const kwargs = {
        ...mainContext.getPostKWArgs(),
        ...(idsData ? { dataset: idsData.dataset } : {}),
      };
      const token = await authContext.getIdToken();
      const res = await axios.post(
        '/get_cross_media_params',
        { countryCode, kwargs },
        {
          headers: { Authorization: token },
        }
      );
      dispatch({
        type: 'SET_INDUSTRY_WEIGHTS',
        payload: res.data.industry_weights,
      });
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      mainContext.loadingDecrement(type);
    }
  };

  const getCrossMediaProperties = async ({
    selectedData = null,
    type = CROSSMEDIATYPE,
    idsData = false, // do not edit the context, just set clean defaults
    preset = 'Default',
  } = {}) => {
    try {
      mainContext.loadingIncrement(type);
      selectedData = selectedData || state.selectedData; // Arima | Rogers Sports & Media
      const countryCode = idsData
        ? idsData.dataset.includes('ca')
          ? 'ca'
          : 'us'
        : authContext.userCountryCode;
      const kwargs = {
        ...mainContext.getPostKWArgs(),
        ...(idsData ? { dataset: idsData.dataset } : {}),
      };
      const token = await authContext.getIdToken();
      const { data } = await axios.post(
        `/get_cross_media_properties`,
        {
          countryCode: countryCode,
          publisher: selectedData,
          preset: preset,
          kwargs,
        },
        { headers: { Authorization: token } }
      );

      const previouslySelectedMediaIds = getSelectedMedias().map(
        (media) => media.id
      );

      //////////////////////////////////////////////////
      // handle media tree

      // transform media tree to fit cross media's needs
      const transformNode = (node, transformFunction) => {
        // Apply the transformation to the current node
        transformFunction(node);

        // If the node has children, transform each of them recursively
        if (node.children && node.children.length > 0) {
          for (let child of node.children) {
            transformNode(child, transformFunction);
          }
        }
      };

      const transformFunction = (node) => {
        // mutate and rename node properties
        for (const type of ['CPM', 'CPP']) {
          if (isNaN(Number(node[type]))) {
            node[type] = 0;
          } else {
            node[type] = parseFloat(node[type]);
          }
        }
        node.CPM = parseFloat(node.CPM);

        // Assign default values.
        Object.assign(node, {
          selected: previouslySelectedMediaIds.includes(node.id),
          isCustomMedia: false,
          displayName: node.display_name,
          hasCPM: !!node.CPM,
          hasCPP: !!node.CPP,
          priceCPM: node.CPM,
          priceCPP: node.CPP,
          priceType: node.default_prices,
          budgetType: 'weekly',
          efficiency: 1,
          min: 0,
          max: 20000,
        });

        // If default_prices is 'CPP' but node.CPP is 0, switch it to CPM and vice versa
        if (node.default_prices === 'CPP' && !node.CPP) {
          node.priceType = 'CPM';
        } else if (node.default_prices === 'CPM' && !node.CPM) {
          node.priceType = 'CPP';
        }

        // delete unused properties
        ['display_name', 'default_prices', 'roas', 'CPM', 'CPP'].forEach(
          (prop) => delete node[prop]
        );
      };

      for (const rootNode of data.medias) {
        transformNode(rootNode, transformFunction);
      }
      if (!idsData) {
        // set medias and company medium in context if no idsData provided
        // in reports, we provide idsData and context from tabs, so only
        // defaults needed if we have idsData
        dispatch({ type: 'SET_MEDIAS', payload: data.medias });
      }
      dispatch({
        type: 'SET_MEDIAS_DEFAULT',
        payload: { medias: data.medias, publisher: selectedData },
      });
      dispatch({ type: 'SET_PUBLISHERS', payload: data.publishers });
    } catch (error) {
      mainContext.handleError(error);
    } finally {
      mainContext.loadingDecrement(type);
    }
  };

  const getMMMs = async ({
    type = CROSSMEDIATYPE,
    idsData = false, // do not edit the context, just set clean defaults
  } = {}) => {
    try {
      mainContext.loadingIncrement(type);
      const token = await authContext.getIdToken();
      const { data } = await axios.post(
        '/get_models',
        {
          extraProperties: ['metadata'],
          countryCode: authContext.userCountryCode,
          kwargs: mainContext.getPostKWArgs(),
        },
        {
          headers: { Authorization: token },
        }
      );
      if (data) {
        const filteredData = data.filter((obj) => obj.metadata);
        const mmms = filteredData.map((obj) => {
          obj.id = obj.id.toString();
          obj.selected = false;
          return obj;
        });
        if (!idsData) {
          // see 'getCrossMediaProperties' for explanation
          dispatch({ type: 'SET_MMMS', payload: mmms });
        }
        dispatch({ type: 'SET_MMMS_DEFAULT', payload: mmms });
      }
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      mainContext.loadingDecrement(type);
    }
  };

  const getDMAs = async ({
    selectedData = null,
    type = CROSSMEDIATYPE,
    idsData = false, // do not edit the context, just set clean defaults
  } = {}) => {
    try {
      mainContext.loadingIncrement(type);
      selectedData = selectedData || state.selectedData;
      const countryCode = idsData
        ? idsData.dataset.includes('ca')
          ? 'ca'
          : 'us'
        : authContext.userCountryCode;
      const kwargs = {
        ...mainContext.getPostKWArgs(),
        ...(idsData ? { dataset: idsData.dataset } : {}),
      };
      const token = await authContext.getIdToken();
      const { data } = await axios.post(
        `/get_cmas_or_dmas`,
        {
          countryCode: countryCode,
          publisher: selectedData,
          kwargs,
        },
        { headers: { Authorization: token } }
      );
      if (!idsData) {
        // set medias and company medium in context if no idsData provided
        // in reports, we provide idsData and context from tabs, so only
        // defaults needed if we have idsData
        dispatch({ type: 'SET_DMAS', payload: data });
      }
      dispatch({ type: 'SET_DMAS_DEFAULT', payload: data });
    } catch (err) {
      mainContext.handleError(err);
    } finally {
      mainContext.loadingDecrement(type);
    }
  };

  ///////////////
  // tabs related

  const getTabs = () => {
    // return sorted list of tabs
    return Object.values(state.tabs).sort((a, b) => a.index - b.index);
  };

  ////////////////
  // media related

  /**
   * Recursively searches for a node within a tree (or its subset) by its name.
   *
   * @param {Array} tree - The tree (or subset) where each node has a 'name' and optional 'children' property.
   *                       Typically, this is `state.medias` or any subset of that tree.
   * @param {string} name - The name of the node to search for.
   *
   * @returns {Object|null} - Returns the node with the specified name if found; otherwise, returns null.
   */
  const _findNodeByName = (tree, name) => {
    for (let node of tree) {
      if (node.name === name) {
        return node;
      }
      if (node.children) {
        const found = _findNodeByName(node.children, name);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * Recursively traverses the media tree to find and return all nodes that are marked as selected.
   *
   * The function uses an inner helper function, `traverse`, to explore each node and its children.
   * Nodes are deemed selected if their 'selected' property is set to true.
   *
   * @returns {Array} - An array of nodes from `state.medias` that have their 'selected' property set to true.
   */
  const getSelectedMedias = () => {
    const traverse = (node) => {
      let nodes = [];
      if (node.selected === true) {
        nodes.push(node);
      }
      if (node.children && node.children.length > 0) {
        for (let child of node.children) {
          nodes = nodes.concat(traverse(child));
        }
      }
      return nodes;
    };

    let selectedNodes = [];
    for (let rootNode of state.medias) {
      selectedNodes = selectedNodes.concat(traverse(rootNode));
    }
    return selectedNodes;
  };

  /**
   Toggle a media's status between selected and deselected. You can set an
   explicit `value` as 'true', 'false', or 'indeterminate', or leave it blank
   for a default toggle action (preferred method).

   When toggling a node, parent nodes are affected accordingly:
   - If any child node is active, its parent nodes are set to 'indeterminate'.
   - If all child nodes are inactive, their parent nodes will also be inactive.
   Those actions will propagate upwards to the root.

   Note: Careful with explicitly setting values, as it will not propagate downwards.
   */
  const toggleSelectedMedia = (targetNode, explicitValue = null) => {
    const propagateUpwards = (node) => {
      if (!node.parent) {
        return;
      }

      const parentNode = _findNodeByName(state.medias, node.parent);
      if (parentNode.selected !== true) {
        // if parent is not explicitly selected, see if it should be indeterminate or not
        const childStates = parentNode.children.map((n) => n.selected);
        if (
          childStates.some(
            (state) => state === true || state === 'indeterminate'
          )
        ) {
          // some children are checked, set as indeterminate if not already selected
          parentNode.selected = 'indeterminate';
        } else {
          // none are checked, set as off if not already explicitly selected
          parentNode.selected = false;
        }
        propagateUpwards(parentNode);
      }
    };

    const node = _findNodeByName(state.medias, targetNode.name);

    if (explicitValue !== null) {
      // explicitly set a value
      node.selected = explicitValue;
      propagateUpwards(node);
    } else {
      // normal scenario -- user clicked on a node to toggle
      if (node.selected === true) {
        // 1. node is checked - either turn off, or set as indeterminate if any child is on|indeterminate
        const childStates = node.children.map((n) => n.selected);
        if (
          childStates.some(
            (state) => state === true || state === 'indeterminate'
          )
        ) {
          // 1.1 has a child set as on|indeterminate, set as indeterminate instead of off
          node.selected = 'indeterminate';
        } else {
          // 1.2 no children are on|indeterminate, turn off
          node.selected = false;
        }
      } else {
        // 2. node is unchecked (false|undefined|indeterminate), turn on.
        node.selected = true;
      }
    }
    // regardless what scenario, propagate upwards
    propagateUpwards(node);
    dispatch({
      type: 'SET_MEDIAS',
      payload: [...state.medias], // shallow copy
    });
  };

  /**
   * Updates the specified media object with the given key-value pairs and then dispatches an action
   * to update the state's media list.
   *
   * @param {Object} media - The media object to update.
   * @param {Object} kwargs - An object containing key-value pairs used to update the media.
   *
   * @returns {void}
   */
  const updateMedia = (media, kwargs) => {
    Object.assign(media, kwargs); // updates the media object with the key-value pairs in kwargs
    dispatch({
      type: 'SET_MEDIAS',
      payload: [...state.medias], // shallow copy
    });
  };

  /**
   * Adds a custom media to the medias list using the first medias as template.
   *
   */
  const addCustomMedia = () => {
    // default display names are 'Custom Media 1', 'Custom Media 2', etc.
    // but if one was to delete 'Custom Media 1', the next custom media should
    // be 'Custom Media 3' and not 'Custom Media 2'. So need to find the
    // max custom media (instead of length) and increment it.
    const customMediaNumbers = state.medias
      .filter((m) => m.isCustomMedia)
      .map((m) => parseInt(m.name.replace('custom_media_', '')))
      .filter((num) => !isNaN(num));
    const maxNumber = Math.max(...customMediaNumbers, 0);

    // Retrieve the first media from the state as a template -> construct custom
    const templateMedia = state.medias[0];
    const newMedia = {
      ...templateMedia,
      selected: true,
      isCustomMedia: true,
      unsetMedia: true, // hasn't been acknowledged yet
      name: `custom_media_${maxNumber + 1}`,
      displayName: `Custom Media ${maxNumber + 1}`,
      estimatedReach: 25,
      color: '#009688',
      priceType: 'CPM',
      hasCPM: true,
      hasCPP: true,
      priceCPM: 20,
      priceCPP: 20,
      parent: null,
      children: null,
    };

    // Dispatch an action to update the state with the new media.
    dispatch({
      type: 'SET_MEDIAS',
      payload: [...state.medias, newMedia], // Add the new media to the existing list.
    });
  };

  /**
   * Removes a custom media from the medias list based on its name.
   */
  const removeCustomMedia = (media) => {
    const updatedMedias = state.medias.filter((m) => m.name !== media.name);
    dispatch({
      type: 'SET_MEDIAS',
      payload: updatedMedias,
    });
  };

  /**
   * Sets the publisher and updates related state.
   *
   * @param {Object} publisher - The publisher data to set.
   * @param {string} [preset='Default'] - The preset to use.
   */
  const setPublisher = (publisher, preset = 'Default') => {
    dispatch({ type: 'SET_PUBLISHER', payload: publisher });
    dispatch({ type: 'SET_SELECTED_DATA', payload: publisher });
    getCrossMediaProperties({ selectedData: publisher, preset });
  };

  /**
   * Updates the publisher and triggers updates to preset.
   *
   * @param {Object} publisher - The publisher data to update.
   * @param {string} [preset='Default'] - The preset to use.
   */
  const updatePublisher = (publisher, preset = 'Default') => {
    setPublisher(publisher, preset);
    setPreset(preset);
    getCrossMediaParams();
    updatePresets(publisher);
  };

  /**
   * Sets the current preset. This function does not trigger updates to other related properties such as Media Channels values.
   * For external usage, consider `updatePreset` to ensure proper state synchronization or handle updates separately.
   *
   * @param {string} preset - The preset to set.
   */
  const setPreset = (preset) => {
    dispatch({ type: 'SET_PRESET', payload: preset });
  };

  /**
   * Sets the presets list which is used in the presets selection dropdown.
   *
   * @param {Array} presets - The list of presets to set.
   */
  const setPresets = (presets) => {
    dispatch({ type: 'SET_PRESETS', payload: presets });
  };

  /**
   * Updates the current preset and triggers cross-media updates.
   *
   * @param {string} preset - The preset to update.
   */
  const updatePreset = (preset) => {
    setPreset(preset);
    getCrossMediaProperties({ preset });
    getCrossMediaParams();
  };

  const addPresetToCrossContext = (newPreset) => {
    dispatch({
      type: 'SET_PRESETS',
      payload: [...state.presets, newPreset], // add the new value to the existing array
    });
  };

  const deletePresetFromCrossContext = (presetToDelete) => {
    // Filters presets to include only the ones that we don't want to delete
    const updatedPresets = state.presets.filter(
      (preset) => preset !== presetToDelete
    );

    dispatch({
      type: 'SET_PRESETS',
      payload: updatedPresets,
    });
  };

  //////////////
  // MMM related

  const getSelectedMMMs = () => {
    return state.MMMs.filter((mmm) => mmm.selected);
  };

  const getSelectedDMAs = () => {
    return state.DMAs.filter((dma) => dma.selected);
  };

  const toggleSelectedMMM = (MMM, value = undefined) => {
    /*
    Toggle an MMM to selected/deselected by explicitly setting a `value` to
    'true' or 'false' or leave blank to toggle on/off.
     */
    if (MMM.selected === value) return;
    if (typeof value === 'undefined') value = !MMM.selected;
    dispatch({
      type: 'SET_MMMS',
      payload: state.MMMs.map((mmm) => {
        if (mmm.id === MMM.id) mmm.selected = value;
        else mmm.selected = false;
        return mmm;
      }),
    });
  };

  ////////////
  // Tracking

  /**
   * Captures an event for tracking purposes. If the event is a workflow start event,
   * it generates a workflowId and stores it. If it's a workflow end event, it clears
   * the stored workflowId after sending the event.
   *
   * Properties we care about are "workflow_id" and "tab_workflow_id". captureEvent will try to
   * append them from state.workflowId and state.tabWorkflowId, or generate new ones for start events.
   *
   * @param {string} eventName - The name of the event to track
   * @param {Object} extraProperties - Additional properties to include with the event
   * @returns {Object} - Returns the event properties
   */
  const captureEvent = (eventName, extraProperties = {}) => {
    // Capture a CMP event - used to track workflows from start to finish and answer questions like
    // How long does it take to complete a CMP? or Where to users drop off?
    let workflowId = state.workflowId;
    let tabWorkflowId = state.tabWorkflowId;

    // Workflow IDs identify a sequence of events. We start a sequence on START and end it on EXIT.
    // So we set/unset the workflow ID on START and EXIT.
    if (eventName === EVENT_NAMES.CROSSMEDIA_START) {
      workflowId = setNewWorkflowId(); // set and get a new workflow ID
    } else if (
      eventName === EVENT_NAMES.CROSSMEDIA_TAB_START ||
      eventName === EVENT_NAMES.CROSSMEDIA_TAB_DUPLICATE ||
      eventName === EVENT_NAMES.CROSSMEDIA_TAB_EDIT
    ) {
      tabWorkflowId = setNewTabWorkflowId(); // set and get a new tab workflow ID
    } else if (eventName === EVENT_NAMES.CROSSMEDIA_TAB_COMPLETE) {
      tabWorkflowId = state.tabWorkflowId; // get the current tab workflow ID
      clearTabWorkflowId(); // clear for next events
    } else if (eventName === EVENT_NAMES.CROSSMEDIA_EXIT) {
      workflowId = state.workflowId; // get the current workflow ID
      clearWorkflowId(); // clear for next events
    }

    // Capture the event
    const eventProps = {
      workflow_id: workflowId,
      tab_workflow_id: tabWorkflowId,
      ...extraProperties,
    };
    if (eventProps.workflow_id || eventProps.tab_workflow_id) {
      // Only capture events if we have a workflow ID or tab workflow ID
      // Reports don't have workflow IDs, and we don't want to track them
      trackerService.captureEvent(eventName, authContext.userInfo, eventProps);
    }
    return eventProps;
  };

  const setNewWorkflowId = () => {
    const newWorkflowId = uuidv4();
    dispatch({ type: 'SET_WORKFLOW_ID', payload: newWorkflowId });
    return newWorkflowId;
  };

  const setNewTabWorkflowId = () => {
    const newTabWorkflowId = uuidv4();
    dispatch({ type: 'SET_TAB_WORKFLOW_ID', payload: newTabWorkflowId });
    return newTabWorkflowId;
  };

  const clearWorkflowId = () => {
    dispatch({ type: 'CLEAR_WORKFLOW_ID' });
  };

  const clearTabWorkflowId = () => {
    dispatch({ type: 'CLEAR_TAB_WORKFLOW_ID' });
  };

  ////////
  // Misc

  const toggleSelectedDMAs = (DMA, value = undefined) => {
    if (DMA.selected === value) return;
    if (typeof value === 'undefined') value = !DMA.selected;
    dispatch({
      type: 'SET_DMAS',
      payload: state.DMAs.map((dma) => {
        if (dma.name === DMA) dma.selected = value;
        return dma;
      }),
    });
  };

  // reset all state - to be called when leaving page / switching audiences, country, users, reports
  const refresh = (type = CROSSMEDIATYPE) => {
    captureEvent(EVENT_NAMES.CROSSMEDIA_EXIT);
    mainContext.loadingIncrement(type);
    dispatch({
      type: REFRESH,
      payload: {
        defaultContext: cloneDeep(defaultContext),
        defaultCrossStates: cloneDeep(defaultCrossStates),
        ...defaultContext,
        ...initialState,
      },
    });
    mainContext.loadingDecrement(type);
  };

  const setSelectedData = (selectedData) => {
    dispatch({ type: 'SET_SELECTED_DATA', payload: selectedData });
    getCrossMediaProperties({ selectedData });
  };

  return (
    <CrossMediaContext.Provider
      value={{
        // context
        curvesGraph: state.curvesGraph,
        mediaMixGraph: state.mediaMixGraph,
        crossStates: state.crossStates,
        defaultContext: state.defaultContext,
        medias: state.medias,
        publisher: state.publisher,
        publishers: state.publishers,
        MMMs: state.MMMs,
        DMAs: state.DMAs,
        penetration: state.penetration,
        industryWeights: state.industryWeights,
        previousTab: state.previousTab,
        selectedTab: state.selectedTab,
        tabs: state.tabs,
        selectedData: state.selectedData,
        preset: state.preset,
        presets: state.presets,
        captureEvent,
        setNewWorkflowId,
        setNewTabWorkflowId,
        clearWorkflowId,
        clearTabWorkflowId,
        // methods
        dispatch,
        useState,
        dumpContext,
        loadContext,
        setSelectedData,
        getCrossMediaPenetration,
        getCrossMediaPlan,
        getCrossMediaParams,
        getCrossMediaProperties,
        getMMMs,
        getDMAs,
        setPublisher,
        updatePublisher,
        setPreset,
        updatePreset,
        setPresets,
        updatePresets,
        addPresetToCrossContext,
        deletePresetFromCrossContext,
        // tabs related
        getTabs,
        // media related
        getSelectedMedias,
        toggleSelectedMedia,
        updateMedia,
        addCustomMedia,
        removeCustomMedia,
        // MMM related
        getSelectedMMMs,
        getSelectedDMAs,
        toggleSelectedMMM,
        toggleSelectedDMAs,
        refresh,
      }}
    >
      {props.children}
    </CrossMediaContext.Provider>
  );
}

export default CrossMediaState;
